import DevJSONView from '@/components/DevJSONView';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { DataGrid } from '@mui/x-data-grid';
import React from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NoRowsErrorOverlay, NoRowsOverlay } from '../../table/DataGridComponents/NoRowsOverlay';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import DataGridNumericFormat from '@/components/table/DataGridComponents/DataGridNumericFormat';
import { usePipelineContext } from '@/context/pipelineContext';
import {
	ColumnControlContextProvider,
	useDataGridColumnProps,
} from '@/context/columnControlContext';
import { useSaveStageLabelMutation } from '@/features/pipelines/pipelineApi';
import { showToast } from '@/features/toast/toastSlice';

const columns = [
	{
		field: 'label',
		headerName: 'Label',
		flex: 2,
		minWidth: 200,
		editable: true,
		sortable: true,
		// renderHeader: (params) => {
		// 	return (
		// 		<Typography sx={{ fontWeight: '700' }}>
		// 			{params.colDef.headerName.toUpperCase()}
		// 		</Typography>
		// 	);
		// },
		display: 'flex',
	},

	{
		field: 'order',
		headerName: 'Order',
		flex: 0.5,
		minWidth: 50,
		editable: true,
		sortable: true,
		display: 'flex',
		// renderHeader: (params) => {
		// 	return (
		// 		<Typography sx={{ fontWeight: '700' }}>
		// 			{params.colDef.headerName.toUpperCase()}
		// 		</Typography>
		// 	);
		// },
		renderEditCell: (params) => <DataGridNumericFormat {...params} />,
	},
	{
		field: 'defaultLabel',
		headerName: 'Default Name',
		flex: 2,
		minWidth: 200,
		editable: false,
		sortable: true,
		// renderHeader: (params) => {
		// 	return (
		// 		<Typography sx={{ fontWeight: '700' }}>
		// 			{params.colDef.headerName.toUpperCase()}
		// 		</Typography>
		// 	);
		// },
		display: 'flex',
	},
];

const DealStageTable = ({ pipelineId }) => (
	<ColumnControlContextProvider variant='dealStages' value={pipelineId}>
		<DealStageTableContent pipelineId={pipelineId} />
	</ColumnControlContextProvider>
);

const DealStageTableContent = ({ pipelineId }) => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const [rowSelectionModel, setRowSelectionModel] = useState([]);
	const [sortModel, setSortModel] = useState([
		{
			field: 'displayOrder',
			sort: 'desc',
		},
	]);

	const [selectedRow, setSelectedRow] = useState(null);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const canEditLabels = userDetails?.principleUser;

	const {
		stages,
		isLoading: isPipelineLoading,
		...pipelineQuery
	} = usePipelineContext(pipelineId);

	const rows = stages ?? [];

	const [saveStageLabel, { isLoading: isUpdateLoading }] = useSaveStageLabelMutation();

	const { columns: columnsWithWidths, onColumnWidthChange } = useDataGridColumnProps(columns, {
		disableColumnVisibility: true,
	});

	const handleSave = async ({ stageId, pipelineId, label = '' }) => {
		console.log('Saving config doc', stageId, pipelineId, label);
		if (label.length === 0) return;

		return await saveStageLabel({
			stageId,
			pipelineId,
			label,
		})
			.unwrap()
			.then((res) => {
				console.log('Update stage label res', res);
				dispatch(
					showToast({
						message: 'Successfully updated stage label',
						success: true,
					}),
				);
				return true;
			})
			.catch((e) => {
				console.error(
					`Error updating stage label stage=${stageId} pipeline=${pipelineId}`,
					e,
				);
				dispatch(
					showToast({
						message: 'Error updating stage label; please try again.',
						error: true,
					}),
				);
				return false;
			});
	};

	return (
		<Box
			sx={{
				border: `1px solid ${theme.palette.divider}`,
				borderRadius: '8px',
				backgroundColor: theme.palette.background.paper,
				'& .MuiDataGrid-root': {
					borderRadius: '8px',
				},
				height: '50vh',
				maxHeight: '100%',
			}}
		>
			<DataGrid
				sx={{
					'& .MuiDataGrid-editInputCell': {
						backgroundColor: 'transparent',
					},
					'--DataGrid-overlayHeight': '3em',
					'& .MuiDataGrid-cell': {
						padding: 1,
						minHeight: '42px',
					},
					'& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell': {
						borderBottom: 'none',
					},
					border: 'none',
				}}
				loading={isPipelineLoading}
				width='100%'
				getRowHeight={() => 'auto'}
				processRowUpdate={async (newRow, oldRow) => {
					console.log('processRowUpdate', { newRow, oldRow });
					if (newRow.label.trim() === oldRow.label.trim() || !newRow.label.trim())
						return oldRow;

					const success = await handleSave({
						stageId: newRow.stageId,
						pipelineId: newRow.pipelineId,
						label: newRow.label.trim(),
					});
					return success ? newRow : oldRow;
				}}
				rows={rows}
				columns={columnsWithWidths}
				onColumnWidthChange={onColumnWidthChange}
				isCellEditable={(params) => {
					if (!canEditLabels || isUpdateLoading) return false;
					return params.colDef.editable;
				}}
				onRowSelectionModelChange={(model) => {
					const rowId = model?.[0];
					setRowSelectionModel(model);
					setSelectedRow(rows.find((r) => r.id === rowId));
				}}
				initialState={{
					sorting: {
						sortModel,
					},
				}}
				sortingOrder={['desc', 'asc']}
				rowSelectionModel={rowSelectionModel}
				disableColumnFilter
				disableColumnSelector
				disableColumnMenu
				onRowClick={(_, e) => {
					e.stopPropagation();
				}}
				hideFooter
				slots={{
					noRowsOverlay: pipelineQuery.isError
						? () => <NoRowsErrorOverlay objectType={'stages'} />
						: () => <NoRowsOverlay objectType={'stages'} />,
				}}
				sortModel={sortModel}
				onSortModelChange={(newModel) => setSortModel(newModel)}
			/>
		</Box>
	);
};

export default DealStageTable;
